import { FC, useRef, useLayoutEffect, useState, useEffect } from 'react'
import { Space } from 'antd'
import { FileView, PrimaryIconButton, Card } from '../index'
import { Edit, Trash } from '../../../assets/icons'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import LinkIcon from '../../../assets/icons/LinkIcon'
import style from './style.m.less'

// We modify DocumentCardProps slightly to match the new local-state pattern
export interface NewDocumentCardProps {
    doc: any
    isAdmin: boolean
    isEditingLoading: boolean
    isDeletingLoading: boolean
    onEdit: (doc: any) => void
    onDelete: (id: string) => void
    handleFileClick: (doc: any) => void
    loadingDownLoad: any
    downloadedId?: string
    width?: number | string
}

const DocumentCard: FC<NewDocumentCardProps> = ({
    doc,
    isAdmin,
    onEdit,
    onDelete,
    isEditingLoading,
    isDeletingLoading,
    handleFileClick,
    loadingDownLoad,
    downloadedId,
    width,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [showIcon, setShowIcon] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useLayoutEffect(() => {
        if (textRef.current) {
            if (textRef.current.scrollHeight <= 44) {
                setShowIcon(false)
            } else {
                setShowIcon(true)
            }
        }
    }, [doc.comment])

    useEffect(() => {
        let handler = setTimeout(() => null, 0)
        const handleClickOutside = (event: any) => {
            if (!event?.target?.closest(`#card-${doc.id}`)) {
                handler = setTimeout(() => {
                    setIsOpen(false)
                }, 200)
            }
        }
        document.addEventListener('mouseup', handleClickOutside)
        return () => {
            clearTimeout(handler)
            document.removeEventListener('mouseup', handleClickOutside)
        }
    }, [doc.id])

    return (
        <div
            style={{
                padding: 0,
                width: width ?? '100%',
                maxWidth: '100%',
                minHeight: '147px',
                height: 'auto',
            }}
            id={`card-${doc.id}`}
        >
            <Card
                className={style.card}
                style={{
                    zIndex: isOpen ? '1000' : 0,
                    width: width ? '100%' : undefined,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        padding: '24px',
                        boxSizing: 'border-box',
                    }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            ref={textRef}
                            className={style.blockWithText}
                            style={{
                                padding: 0,
                                height: isOpen ? 'auto' : '44px',
                                minHeight: '44px',
                                display: isOpen ? 'block' : '-webkit-box',
                            }}
                        >
                            {doc?.comment || ''}
                        </div>
                    </div>

                    {isAdmin && (
                        <Space
                            style={{
                                marginTop: '0px',
                                alignItems: 'unset',
                            }}
                        >
                            <PrimaryIconButton
                                customStyle={{ marginLeft: '16px' }}
                                icon={<Edit />}
                                onClick={() => onEdit(doc)}
                                loading={isEditingLoading}
                            />
                            <PrimaryIconButton
                                isCancel
                                icon={<Trash />}
                                onClick={() => onDelete(doc.id)}
                                loading={isDeletingLoading}
                            />
                        </Space>
                    )}
                    {showIcon && (
                        <div
                            style={{
                                margin: '0 4px 0 16px',
                                paddingTop: '6px',
                                cursor: 'pointer',
                            }}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? (
                                <UpOutlined style={{ pointerEvents: 'none' }} />
                            ) : (
                                <DownOutlined style={{ pointerEvents: 'none' }} />
                            )}
                        </div>
                    )}
                </div>

                {doc.link_url !== '' && doc.link_name !== '' ? (
                    <a
                        href={doc.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className={style.docsLink}
                        style={{
                            display: 'flex',
                            gap: 8,
                            padding: '0 24px',
                            alignItems: 'center',
                        }}
                    >
                        <LinkIcon />
                        <span className={style.docsLinkText}>{doc.link_name}</span>
                    </a>
                ) : (
                    <FileView
                        style={{ padding: '0 24px', width: '100%', maxWidth: '100%' }}
                        fileTitleStyle={{ maxWidth: '100%' }}
                        title={doc.document?.file_name || ''}
                        size={doc.document?.file_size || 0}
                        onClick={() => handleFileClick(doc)}
                        loading={
                            loadingDownLoad?.[doc.document?.id] ||
                            (loadingDownLoad && downloadedId === doc.document?.id)
                        }
                    />
                )}
            </Card>
        </div>
    )
}

export default DocumentCard
